import { KeyRoundIcon, LogInIcon } from 'lucide-react';

import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  linkMethodOf,
  linkMethodWords,
  showsExpiry,
  statusWords,
} from '@/features/identity';
import type { PersonLinkView } from '@/lib/api/generated/model';

import { day, daysLeft, linkStanding } from '../linkedAccounts';
import {
  COMMIT_IDENTITY_COPY,
  LINKED_ACCOUNTS_COPY as COPY,
} from '../linkedAccountsCopy';

const DOT: Record<ReturnType<typeof linkStanding>, string> = {
  fine: 'bg-state-success',
  expiring: 'bg-state-warning',
  broken: 'bg-state-danger',
};

/**
 * One person's link on one host, in one line (spec 162 US1): how it was made,
 * whose account it is, and whether it works. The days left show only when a
 * token is about to end. The full words are in the tooltip, and in text for
 * a screen reader: the dot's colour is never the only signal (FR-008).
 */
export function LinkChip({
  link,
  now,
}: {
  link: PersonLinkView | null;
  now: Date;
}) {
  if (!link) {
    return (
      <span className="border-border text-muted-foreground inline-flex items-center rounded-full border border-dashed px-2.5 py-0.5 text-xs whitespace-nowrap">
        {COPY.notLinked}
      </span>
    );
  }

  const method = linkMethodOf(link);
  const standing = linkStanding(link, now);
  const days = standing === 'expiring' ? daysLeft(link, now) : null;
  const status = statusWords(link);
  const expires = showsExpiry(link) ? day(link.expires_at) : null;
  const MethodIcon = method === 'oauth' ? LogInIcon : KeyRoundIcon;
  const detail = [
    linkMethodWords(method),
    status,
    expires ? COPY.expires(expires) : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <span
            // Focusable so the tooltip's words reach a keyboard too.
            tabIndex={0}
            className="border-border bg-card text-foreground focus-visible:ring-ring inline-flex max-w-full items-center gap-1.5 rounded-full border py-0.5 ps-1 pe-2.5 text-xs whitespace-nowrap focus-visible:ring-3 focus-visible:outline-none"
          />
        }
      >
        <span className="bg-muted text-muted-foreground inline-grid size-5 shrink-0 place-items-center rounded-full">
          <MethodIcon aria-hidden="true" className="size-3" strokeWidth={2} />
          <span className="sr-only">{linkMethodWords(method)}</span>
        </span>
        <span className="min-w-0 truncate">@{link.forge_username}</span>
        <span
          aria-hidden="true"
          className={`size-2 shrink-0 rounded-full ${DOT[standing]}`}
        />
        <span className="sr-only">{status}</span>
        {days !== null ? (
          <span className="text-state-warning tabular-nums">
            {COMMIT_IDENTITY_COPY.daysLeft(days)}
          </span>
        ) : null}
      </TooltipTrigger>
      <TooltipContent>{detail}</TooltipContent>
    </Tooltip>
  );
}
